import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { DataHelper } from "src/services/dataHelper";

const CACHE_KEY = "translations_cache";

export const useDynamicTranslations = () => {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(true);

  const loadTranslations = useCallback(async () => {
    try {
      setLoading(true);

      // 1️⃣ Load cached data first (to prevent blank screen)
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          const translations = parsed.data;
          if (translations?.length) {
            const en = {};
            const ar = {};
            translations.forEach((item) => {
              const key = item.keyName || item.key;
              if (!key) return;
              en[key] = item.englishName || item.en || key;
              ar[key] = item.arabicName || item.ar || key;
            });
            i18n.addResourceBundle("en", "translation", en, true, true);
            i18n.addResourceBundle("ar", "translation", ar, true, true);
          }
        } catch {
          console.warn("⚠️ Failed to read cached translations");
        }
      }

      // 2️⃣ Always fetch fresh data from API
      const res = await new DataHelper().getList("Translate", 100000, 1);
      const translations = res?.data?.list || [];

      // 3️⃣ Save new cache
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ data: translations, timestamp: Date.now() })
      );

      // 4️⃣ Update i18n with latest translations
      const en = {};
      const ar = {};
      translations.forEach((item) => {
        const key = item.keyName || item.key;
        if (!key) return;
        en[key] = item.englishName || item.en || key;
        ar[key] = item.arabicName || item.ar || key;
      });
      //console.log(JSON.stringify(translations))
      i18n.addResourceBundle("en", "translation", en, true, true);
      i18n.addResourceBundle("ar", "translation", ar, true, true);
    } catch (err) {
      console.error("❌ Failed to load translations:", err);
    } finally {
      setLoading(false);
    }
  }, [i18n]);

  useEffect(() => {
    loadTranslations();
  }, [loadTranslations, i18n.language]); // re-run when language changes

  return { loading, reloadTranslations: loadTranslations };
};
